import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface AddUserDialogData {
	userType: 'admin' | 'operator';
	mode: 'create' | 'edit';
	userData?: any;
	assignedEntityName?: string;
	regions?: any[];
	sites?: any[];
}

@Component({
	selector: 'app-add-user-dialog',
	templateUrl: './add-user-dialog.component.html',
	styleUrls: ['./add-user-dialog.component.scss']
})
export class AddUserDialogComponent implements OnInit {
	userForm: FormGroup;
	userType: 'admin' | 'operator';
	mode: 'create' | 'edit';
	regions: any[] = [];
	sites: any[] = [];
	filteredSites: any[] = [];

	constructor(
		private fb: FormBuilder,
		private dialogRef: MatDialogRef<AddUserDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: AddUserDialogData
	) {
		this.userType = data.userType;
		this.mode = data.mode;
		this.regions = data.regions || [];
		this.sites = data.sites || [];
		this.filteredSites = this.sites;
		this.userForm = this.createForm(data.userData);
	}

	ngOnInit(): void {}

	createForm(userData?: any): FormGroup {
		const passwordValidators = this.mode === 'create' 
			? [Validators.required, Validators.minLength(6)] 
			: [];

		let fullName = '';
		let username = '';
		let email = '';
		let mobile = '';
		let emp_code = '';
		let regionId = '';

		if (userData) {
			if (userData.userDetails) {
				fullName = userData.userDetails.name || userData.fullName || '';
				email = userData.userDetails.email || userData.email || '';
			} else {
				fullName = userData.fullName || userData.name || '';
				email = userData.email || '';
			}
			
			username = userData.username || '';
			mobile = userData.mobile || '';
			emp_code = userData.emp_code || '';
			
			if (userData.regions && userData.regions.length > 0) {
				regionId = userData.regions[0].id || '';
			}
		}

		const formGroup = this.fb.group({
			fullName: [fullName, [Validators.required, Validators.minLength(2)]],
			username: [username, [Validators.required, Validators.minLength(3)]],
			email: [email, [Validators.required, Validators.email]],
			mobile: [mobile, [Validators.pattern(/^[0-9+\-\s()]+$/)]],
			password: ['', passwordValidators],
			emp_code: [emp_code]
		});

		if (this.userType === 'admin') {
			const regionValidators = this.mode === 'create' ? [Validators.required] : [];
			
			let adminRegionId = '';
			if (this.mode === 'edit' && userData && userData.regions && userData.regions.length > 0) {
				adminRegionId = userData.regions[0].id || '';
			}
			
			formGroup.addControl('regionId', this.fb.control(adminRegionId, regionValidators));
		}

		if (this.userType === 'operator') {
			const regionValidators = this.mode === 'create' ? [Validators.required] : [];
			const siteValidators = this.mode === 'create' ? [Validators.required] : [];
			
			let operatorRegionId = '';
			let operatorSiteIds: string[] = [];
			
			if (this.mode === 'edit' && userData) {
				if (userData.sites && userData.sites.length > 0) {
					operatorSiteIds = userData.sites.map((site: any) => site.id || '');
					operatorRegionId =  userData.sites[0].regionId || '';
				} else if (userData.regions && userData.regions.length > 0) {
					operatorRegionId = userData.regions[0].id || '';
				}
			}
			
			formGroup.addControl('operatorRegionId', this.fb.control(operatorRegionId, regionValidators));
			formGroup.addControl('operatorSiteIds', this.fb.control(operatorSiteIds, siteValidators));
			
			// Set up region change listener for site filtering
			const operatorRegionControl = formGroup.get('operatorRegionId');
			if (operatorRegionControl) {
				operatorRegionControl.valueChanges.subscribe((regionId: string) => {
					this.onRegionChange(regionId);
					// Only reset site selection if we're not in edit mode initialization
					if (this.mode === 'create') {
						const operatorSiteControl = formGroup.get('operatorSiteIds');
						if (operatorSiteControl) {
							operatorSiteControl.setValue([]);
						}
					}
				});
			}
			
			// Initialize filtered sites based on selected region
			if (operatorRegionId) {
				this.onRegionChange(operatorRegionId);
			}
		}

		return formGroup;
	}

	getAssignedEntityLabel(): string {
		return this.userType === 'admin' ? 'Assigned Region' : 'Assigned Site';
	}

	onSave(): void {
		if (this.userForm.valid) {
			const formData = {
				...this.userForm.value,
				userType: this.userType,
				mode: this.mode
			};

			if (this.mode === 'edit' && this.data.userData && this.data.userData.id) {
				formData.userId = this.data.userData.id;
			}

			if (this.mode === 'edit' && !formData.password) {
				delete formData.password;
			}

			this.dialogRef.close(formData);
		} else {
			this.userForm.markAllAsTouched();
		}
	}

	onCancel(): void {
		this.dialogRef.close(null);
	}

	isFieldInvalid(field: string): boolean {
		const control = this.userForm.get(field);
		return !!control && control.invalid && (control.touched || control.dirty);
	}

	getErrorMessage(field: string): string {
		const control = this.userForm.get(field);
		if (!control) return '';
		if (control.hasError('required')) {
			if (field === 'fullName') return 'Full name is required (minimum 2 characters)';
			if (field === 'username') return 'Username is required (minimum 3 characters)';
			if (field === 'email') return 'Valid email address is required';
			if (field === 'password') return 'Password is required (minimum 6 characters)';
		}
		if (control.hasError('minlength')) {
			if (field === 'fullName') return 'Full name must be at least 2 characters';
			if (field === 'username') return 'Username must be at least 3 characters';
			if (field === 'password') return 'Password must be at least 6 characters';
		}
		if (control.hasError('email')) {
			return 'Please enter a valid email address';
		}
		if (control.hasError('pattern') && field === 'mobile') {
			return 'Please enter a valid mobile number';
		}
		return '';
	}

	mobileFieldError(fieldName: string): boolean {
		const control = this.userForm.get(fieldName);
		return !!control && control.hasError('pattern') && (control.touched || control.dirty);
	}

	markAllAsTouched(): void {
		this.userForm.markAllAsTouched();
	}

	getUserDisplayName(): string {
		if (!this.data.userData) return 'New User';
		const userData = this.data.userData;
		
		// Try different sources for the display name
		if (userData.userDetails && userData.userDetails.name) {
			return userData.userDetails.name;
		}
		
		return userData.fullName || userData.name || userData.username || 'Unknown User';
	}

	getTitle(): string {
		if (this.mode === 'create') {
			return `Create ${this.userType === 'admin' ? 'Admin' : 'Operator'}`;
		} else {
			const displayName = this.getUserDisplayName();
			return `Edit ${this.userType === 'admin' ? 'Admin' : 'Operator'}: ${displayName}`;
		}
	}

	onRegionChange(regionId: string): void {
		if (regionId) {
			this.filteredSites = this.sites.filter(site => 
				site.region_id === regionId || site.regionId === regionId
			);
		} else {
			this.filteredSites = this.sites;
		}
	}

	getRegionDisplayLabel(): string {
		return this.userType === 'admin' ? 'Select Region' : 'Select Region';
	}

	getSiteDisplayLabel(): string {
		return 'Select Sites';
	}
}
